import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  PlusCircle, Search, RefreshCw, Wallet, TrendingDown, CheckCircle2,
  XCircle, ChevronRight, Ban, Receipt, AlertTriangle,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { getMexicoTodayDateOnly } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Credit {
  id: number;
  alumnoId: number;
  alumnoNombre: string;
  alumnoNui: string;
  amount: string;
  amountRemaining: string;
  sourceType: string;
  bankAccountName: string;
  status: 'active' | 'fully_applied' | 'voided';
  notes: string | null;
  createdAt: string;
}

interface BankAccount {
  id: number;
  name: string;
  code: string;
  type: string;
}

interface AlumnoResult {
  id: number;
  nombre: string;
  apellidoPaterno: string;
  nui: string;
}

interface Invoice {
  id: number;
  invoiceNumber: string;
  total: string;
  status: string;
  dueDate: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: string | number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n));

const statusBadge = (status: string) => {
  if (status === 'active') return <Badge className="bg-emerald-100 text-emerald-800 border-0">Activo</Badge>;
  if (status === 'fully_applied') return <Badge className="bg-blue-100 text-blue-800 border-0">Aplicado</Badge>;
  return <Badge className="bg-gray-100 text-gray-600 border-0">Anulado</Badge>;
};

const sourceLabel = (s: string) => {
  const m: Record<string, string> = {
    manual: 'Manual',
    overpayment: 'Sobrepago',
    void_invoice: 'Factura Anulada',
    csv_import: 'Importación CSV',
  };
  return m[s] ?? s;
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(path, { credentials: 'include', ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

// ─── New Credit Dialog ────────────────────────────────────────────────────────

function NewCreditDialog({ open, onClose, bankAccounts }: {
  open: boolean;
  onClose: () => void;
  bankAccounts: BankAccount[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [alumnoSearch, setAlumnoSearch] = useState('');
  const [selectedAlumno, setSelectedAlumno] = useState<AlumnoResult | null>(null);
  const [alumnoResults, setAlumnoResults] = useState<AlumnoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(() => getMexicoTodayDateOnly());

  const searchAlumnos = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setAlumnoResults([]); return; }
    setSearching(true);
    try {
      const data = await apiFetch(`/api/alumnos?search=${encodeURIComponent(q)}&limit=10`);
      const list: AlumnoResult[] = (data.alumnos ?? data ?? []).map((a: any) => ({
        id: a.id,
        nombre: a.nombre,
        apellidoPaterno: a.apellidoPaterno || '',
        nui: a.numeroIdentificacionUnico || a.nui || '',
      }));
      setAlumnoResults(list);
    } catch { setAlumnoResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchAlumnos(alumnoSearch), 300);
    return () => clearTimeout(t);
  }, [alumnoSearch, searchAlumnos]);

  const createMutation = useMutation({
    mutationFn: (body: object) => apiFetch('/api/treasury/student-credits', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['student-credits'] });
      qc.invalidateQueries({ queryKey: ['student-credits-active-map'] });
      toast({ title: 'Saldo a Favor registrado', description: 'El crédito fue creado y el asiento contable generado.' });
      handleClose();
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const handleClose = () => {
    setAlumnoSearch(''); setSelectedAlumno(null); setAlumnoResults([]);
    setAmount(''); setBankAccountId(''); setNotes('');
    setDate(new Date().toISOString().split('T')[0]);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedAlumno) return toast({ title: 'Error', description: 'Selecciona un alumno', variant: 'destructive' });
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) return toast({ title: 'Error', description: 'Monto inválido', variant: 'destructive' });
    if (!bankAccountId) return toast({ title: 'Error', description: 'Selecciona una cuenta bancaria', variant: 'destructive' });
    createMutation.mutate({
      alumnoId: selectedAlumno.id,
      amount: parseFloat(amount),
      bankAccountId: parseInt(bankAccountId),
      notes: notes || undefined,
      date,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-violet-600" />
            Registrar Saldo a Favor
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label>Alumno</Label>
            {selectedAlumno ? (
              <div className="flex items-center justify-between p-2 border rounded-md bg-violet-50">
                <div>
                  <p className="font-medium text-sm">{selectedAlumno.nombre} {selectedAlumno.apellidoPaterno}</p>
                  <p className="text-xs text-gray-500">{selectedAlumno.nui}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedAlumno(null); setAlumnoSearch(''); }}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre o NUI..."
                  className="pl-8"
                  value={alumnoSearch}
                  onChange={e => setAlumnoSearch(e.target.value)}
                />
                {searching && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}
                {alumnoResults.length > 0 && !selectedAlumno && (
                  <div className="absolute z-50 w-full bg-white border rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {alumnoResults.map(a => (
                      <button
                        key={a.id}
                        className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm"
                        onClick={() => { setSelectedAlumno(a); setAlumnoResults([]); setAlumnoSearch(''); }}
                      >
                        <span className="font-medium">{a.nombre} {a.apellidoPaterno}</span>
                        <span className="text-gray-400 ml-2 text-xs">{a.nui}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Monto ($)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Fecha</Label>
              <Input type="date" value={date} max={getMexicoTodayDateOnly()} onChange={e => setDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Cuenta Bancaria / Caja</Label>
            <Select value={bankAccountId} onValueChange={setBankAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona cuenta..." />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map(acc => (
                  <SelectItem key={acc.id} value={String(acc.id)}>
                    {acc.code} — {acc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Notas <span className="text-gray-400 font-normal">(opcional)</span></Label>
            <Textarea
              placeholder="Motivo del saldo a favor, referencia de pago, etc."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Guardando...' : 'Registrar Saldo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Apply Credit Dialog ──────────────────────────────────────────────────────

function ApplyCreditDialog({ credit, open, onClose }: {
  credit: Credit | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (!open || !credit) return;
    setSelectedInvoiceId(''); setAmount('');
    setLoadingInvoices(true);
    apiFetch(`/api/sales/invoices?customerId=${credit.alumnoId}&status=ISSUED,PARTIALLY_PAID&limit=50`)
      .then(d => setInvoices(Array.isArray(d) ? d : (d.invoices ?? [])))
      .catch(() => setInvoices([]))
      .finally(() => setLoadingInvoices(false));
  }, [open, credit]);

  const maxAmount = credit ? parseFloat(credit.amountRemaining) : 0;
  const selectedInvoice = invoices.find(i => String(i.id) === selectedInvoiceId);

  const applyMutation = useMutation({
    mutationFn: (body: object) => apiFetch(`/api/treasury/student-credits/${credit!.id}/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['student-credits'] });
      qc.invalidateQueries({ queryKey: ['student-credits-active-map'] });
      toast({
        title: 'Crédito Aplicado',
        description: `Se aplicaron ${fmt(res.amountApplied)} a la factura. Saldo restante: ${fmt(res.creditRemaining)}.`,
      });
      onClose();
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const handleSubmit = () => {
    if (!selectedInvoiceId) return toast({ title: 'Error', description: 'Selecciona una factura', variant: 'destructive' });
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast({ title: 'Error', description: 'Monto inválido', variant: 'destructive' });
    if (amt > maxAmount) return toast({ title: 'Error', description: `El monto no puede superar el saldo disponible (${fmt(maxAmount)})`, variant: 'destructive' });
    applyMutation.mutate({ invoiceId: parseInt(selectedInvoiceId), amount: amt });
  };

  if (!credit) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" />
            Aplicar Saldo a Favor
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="p-3 rounded-lg bg-violet-50 border border-violet-200">
            <p className="text-sm font-medium">{credit.alumnoNombre}</p>
            <p className="text-xs text-gray-500">{credit.alumnoNui}</p>
            <p className="text-sm mt-1">
              Saldo disponible: <strong className="text-violet-700">{fmt(credit.amountRemaining)}</strong>
            </p>
          </div>

          <div className="space-y-1">
            <Label>Factura a Aplicar</Label>
            {loadingInvoices ? (
              <p className="text-sm text-gray-400">Cargando facturas...</p>
            ) : invoices.length === 0 ? (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">No hay facturas pendientes para este alumno.</p>
            ) : (
              <Select value={selectedInvoiceId} onValueChange={v => {
                setSelectedInvoiceId(v);
                const inv = invoices.find(i => String(i.id) === v);
                if (inv) setAmount(String(Math.min(maxAmount, parseFloat(inv.total))));
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona factura..." />
                </SelectTrigger>
                <SelectContent>
                  {invoices.map(inv => (
                    <SelectItem key={inv.id} value={String(inv.id)}>
                      {inv.invoiceNumber} — {fmt(inv.total)} ({inv.status === 'PARTIALLY_PAID' ? 'Parcial' : 'Emitida'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedInvoice && (
            <div className="space-y-1">
              <Label>Monto a Aplicar ($)</Label>
              <Input
                type="number"
                min="0.01"
                max={maxAmount}
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
              />
              <p className="text-xs text-gray-400">
                Máximo: {fmt(maxAmount)} (saldo disponible)
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={applyMutation.isPending || !selectedInvoiceId || invoices.length === 0}
          >
            {applyMutation.isPending ? 'Aplicando...' : 'Aplicar Crédito'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Applications History Dialog ──────────────────────────────────────────────

function ApplicationsDialog({ credit, open, onClose }: {
  credit: Credit | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: apps = [], isLoading } = useQuery({
    queryKey: ['credit-applications', credit?.id],
    queryFn: () => apiFetch(`/api/treasury/student-credits/${credit!.id}/applications`),
    enabled: open && !!credit,
  });

  if (!credit) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Historial de Aplicaciones — {credit.alumnoNombre}</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          {isLoading ? (
            <p className="text-sm text-gray-400">Cargando...</p>
          ) : apps.length === 0 ? (
            <p className="text-sm text-gray-400">Sin aplicaciones registradas.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factura</TableHead>
                  <TableHead>Monto Aplicado</TableHead>
                  <TableHead>Estado Factura</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(apps as any[]).map((a) => (
                  <TableRow key={a.app.id}>
                    <TableCell className="font-mono text-sm">{a.invoiceNumber}</TableCell>
                    <TableCell className="font-medium text-blue-700">{fmt(a.app.amountApplied)}</TableCell>
                    <TableCell>{a.invoiceStatus}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {new Date(a.app.appliedAt).toLocaleDateString('es-MX')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Void Confirmation Dialog ─────────────────────────────────────────────────

function VoidConfirmDialog({ credit, open, onClose, onConfirm, loading }: {
  credit: Credit | null;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  if (!credit) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            ¿Anular Saldo a Favor?
          </DialogTitle>
          <DialogDescription>
            Se anulará el crédito de <strong>{fmt(credit.amountRemaining)}</strong> de{' '}
            <strong>{credit.alumnoNombre}</strong>. Se generará un asiento contable de reversa.
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? 'Anulando...' : 'Sí, Anular'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function StudentCreditsList() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [newCreditOpen, setNewCreditOpen] = useState(false);
  const [applyCredit, setApplyCredit] = useState<Credit | null>(null);
  const [historyCredit, setHistoryCredit] = useState<Credit | null>(null);
  const [voidTarget, setVoidTarget] = useState<Credit | null>(null);

  const { data: credits = [], isLoading, refetch } = useQuery<Credit[]>({
    queryKey: ['student-credits', statusFilter],
    queryFn: () => apiFetch(`/api/treasury/student-credits?status=${statusFilter}&limit=200`),
    staleTime: 30000,
  });

  const { data: bankAccountsRaw = [] } = useQuery({
    queryKey: ['payment-accounts'],
    queryFn: () => apiFetch('/api/administration/payment-accounts'),
    staleTime: 300000,
  });

  const bankAccounts: BankAccount[] = (bankAccountsRaw as any[]).map((a: any) => ({
    id: a.id, name: a.name, code: a.code, type: a.type,
  }));

  const voidMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/treasury/student-credits/${id}/void`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['student-credits'] });
      qc.invalidateQueries({ queryKey: ['student-credits-active-map'] });
      toast({ title: 'Saldo Anulado', description: 'El crédito fue anulado y el asiento de reversa generado.' });
      setVoidTarget(null);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const filtered = credits.filter(c => {
    const q = searchTerm.toLowerCase();
    return !q || c.alumnoNombre.toLowerCase().includes(q) || c.alumnoNui.toLowerCase().includes(q);
  });

  const totalActive = credits
    .filter(c => c.status === 'active')
    .reduce((s, c) => s + parseFloat(c.amountRemaining), 0);
  const countActive = credits.filter(c => c.status === 'active').length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="h-7 w-7 text-violet-600" />
            Saldos a Favor
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Gestión de créditos y anticipos de alumnos</p>
        </div>
        <Button onClick={() => setNewCreditOpen(true)} className="gap-2">
          <PlusCircle className="h-4 w-4" />
          Nuevo Saldo a Favor
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-violet-200 bg-violet-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Wallet className="h-8 w-8 text-violet-500" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo Total Activo</p>
              <p className="text-xl font-bold text-violet-700">{fmt(totalActive)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Créditos Activos</p>
              <p className="text-xl font-bold text-emerald-700">{countActive}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingDown className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total Aplicados</p>
              <p className="text-xl font-bold text-blue-700">
                {credits.filter(c => c.status === 'fully_applied').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o NUI..."
                className="pl-8"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Solo Activos</SelectItem>
                <SelectItem value="fully_applied">Aplicados</SelectItem>
                <SelectItem value="voided">Anulados</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="text-sm font-medium text-gray-600">
            {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-400">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <Wallet className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>No hay saldos a favor{statusFilter === 'active' ? ' activos' : ''} registrados.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Alumno</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead className="text-right">Importe Original</TableHead>
                  <TableHead className="text-right">Saldo Disponible</TableHead>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(credit => (
                  <TableRow key={credit.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{credit.alumnoNombre}</p>
                        <p className="text-xs text-gray-400 font-mono">{credit.alumnoNui}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-gray-600">{sourceLabel(credit.sourceType)}</span>
                    </TableCell>
                    <TableCell className="text-right font-medium">{fmt(credit.amount)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold ${credit.status === 'active' ? 'text-violet-700' : 'text-gray-400'}`}>
                        {fmt(credit.amountRemaining)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{credit.bankAccountName || '—'}</TableCell>
                    <TableCell>{statusBadge(credit.status)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(credit.createdAt).toLocaleDateString('es-MX')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {credit.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs"
                              onClick={() => setApplyCredit(credit)}
                            >
                              <Receipt className="h-3 w-3 mr-1" />
                              Aplicar
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setVoidTarget(credit)}
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-400 hover:text-gray-700"
                          onClick={() => setHistoryCredit(credit)}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <NewCreditDialog
        open={newCreditOpen}
        onClose={() => setNewCreditOpen(false)}
        bankAccounts={bankAccounts}
      />

      <ApplyCreditDialog
        credit={applyCredit}
        open={!!applyCredit}
        onClose={() => setApplyCredit(null)}
      />

      <ApplicationsDialog
        credit={historyCredit}
        open={!!historyCredit}
        onClose={() => setHistoryCredit(null)}
      />

      <VoidConfirmDialog
        credit={voidTarget}
        open={!!voidTarget}
        onClose={() => setVoidTarget(null)}
        onConfirm={() => voidTarget && voidMutation.mutate(voidTarget.id)}
        loading={voidMutation.isPending}
      />
    </div>
  );
}
